import { extractEmailDomain, UserMailboxId } from "@sp-crm/core";
import { DocLink } from "components/app/doc-link";
import { SupportEmailLink } from "components/app/support-email-link";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { ContentFull, ContentHeader, ContentSubheader } from "components/layout";
import { linkStyle } from "components/reports/show/custom-reports/custom-reports-sidebar";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { PrimaryButton, primaryClasses } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import {
    MessageTrackingLevel,
    SmtpAuthenticationType,
    SmtpCredentialStatus,
    useCreateSmtpCredentialMutation,
    useDeleteSmtpCredentialForUserMutation,
    useDeleteUserMailboxMutation,
    useGetMessagingConfigurationQuery,
    useGetSmtpCredentialForUserQuery,
    useGetUserMailboxesQuery,
    UserMailboxStatus,
    WhiteLabelContentMode,
} from "generated/graphql";
import React, { useMemo } from "react";
import { useProductName, useWhiteLabelContentMode } from "store/selectors/branding";
import { useAutoComplete, useCurrentUser, useFeature } from "store/selectors/hooks";
import { stableQueryOptions } from "util/requests";

interface AccountSettingsEmailProps {}

export const AccountSettingsEmail: React.FC<AccountSettingsEmailProps> = props => {
    const [showPanel, setShowPanel] = React.useState(false);
    const enhancedMode = useFeature("enhancedSentMessageView");

    const handleConnectSmtp = React.useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            setShowPanel(true);
        },
        [setShowPanel],
    );

    const handleDismiss = React.useCallback(() => {
        setShowPanel(false);
    }, [setShowPanel]);

    const getCredential = useGetSmtpCredentialForUserQuery({});

    const deleteCredential = useDeleteSmtpCredentialForUserMutation();

    const handleSaved = React.useCallback(() => {
        setShowPanel(false);
        getCredential.refetch();
    }, [setShowPanel, getCredential]);

    const handleDisconnect = React.useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            const confirmed = await fancyConfirm(
                "Disconnect provider?",
                "Are you sure you want to disconnect your email provider? Emails will still be send, but not directly from your email provider.",
                "Yes, disconnect",
                "Cancel",
            );

            if (confirmed) {
                await deleteCredential.mutateAsync({});
                getCredential.refetch();
            }
        },
        [deleteCredential, getCredential],
    );

    const productName = useProductName();
    const messagingConfigurationQuery = useGetMessagingConfigurationQuery(
        {},
        stableQueryOptions(),
    );
    const currentUser = useCurrentUser();
    const mailboxSyncEnabled = useFeature("mailboxSync");

    const infoMessageType: "perRecipient" | "directEmail" | null = useMemo(() => {
        if (
            messagingConfigurationQuery.data &&
            messagingConfigurationQuery.data.getMessagingConfiguration.tenant
                .customDomains.length > 0
        ) {
            const userEmailDomain = extractEmailDomain(currentUser.email);

            const { customDomains, trackingLevel } =
                messagingConfigurationQuery.data.getMessagingConfiguration.tenant;

            const directEmail = customDomains.includes(userEmailDomain);

            if (directEmail) {
                return trackingLevel === MessageTrackingLevel.Recipient && enhancedMode
                    ? "perRecipient"
                    : "directEmail";
            }
        }

        return null;
    }, [currentUser.email, messagingConfigurationQuery.data, enhancedMode]);

    return (
        <div className="space-y-4">
            {mailboxSyncEnabled ? <CreateMailboxIntegration /> : null}
            <ContentFull>
                <ContentHeader>Email Send Integration</ContentHeader>
                <ContentSubheader>
                    Send email directly from your own email provider to improve
                    deliverability and save {productName} emails in your provider&apos;s
                    sent folder.
                </ContentSubheader>
                <QueryRenderer
                    name="AccountSettingsEmail.getSmtpCredential"
                    query={getCredential}>
                    {data => (
                        <div className="space-y-4">
                            {infoMessageType === "perRecipient" ? (
                                <InlineBanner type="info">
                                    Your emails are already optimized for deliverability
                                    through Direct Email. If you are experiencing
                                    deliverability challenges, connecting your email
                                    provider directly via this setting may resolve them,
                                    but it will limit open tracking to a per message
                                    basis. If you have further questions, please reach out
                                    to&nbsp;
                                    <SupportEmailLink />.
                                </InlineBanner>
                            ) : null}
                            {infoMessageType === "directEmail" ? (
                                <InlineBanner type="info">
                                    Your emails are already optimized for deliverability
                                    through Direct Email. If you are experiencing
                                    deliverability challenges, connecting your email
                                    provider directly via this setting may resolve them.
                                    If you have further questions, please reach out
                                    to&nbsp;
                                    <SupportEmailLink />.
                                </InlineBanner>
                            ) : null}
                            {data.getSmtpCredentialForUser ? (
                                <div className="space-y-2">
                                    {data.getSmtpCredentialForUser.status ===
                                    SmtpCredentialStatus.Active ? (
                                        <InlineBanner type="success">
                                            Your email provider is connected. Emails will
                                            be sent directly from your email provider.
                                        </InlineBanner>
                                    ) : null}
                                    {data.getSmtpCredentialForUser.status ===
                                    SmtpCredentialStatus.Disconnected ? (
                                        <InlineBanner type="error">
                                            The username or password provided no longer
                                            authenticate with your email provider. To
                                            reconnect, disconnect the provider then
                                            connect again with new credentials.
                                        </InlineBanner>
                                    ) : null}
                                    {data.getSmtpCredentialForUser.authenticationType ===
                                    SmtpAuthenticationType.Graph ? (
                                        <>
                                            <p>
                                                <strong>Provider:</strong> Outlook.com
                                            </p>
                                            <p>
                                                <strong>Username:</strong>{" "}
                                                {data.getSmtpCredentialForUser.username}
                                            </p>
                                            <p>
                                                <strong>Password:</strong> ********
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <p>
                                                <strong>SMTP Server:</strong>{" "}
                                                {data.getSmtpCredentialForUser.host}
                                            </p>
                                            <p>
                                                <strong>SMTP Port:</strong>{" "}
                                                {data.getSmtpCredentialForUser.port}
                                            </p>
                                            <p>
                                                <strong>Username:</strong>{" "}
                                                {data.getSmtpCredentialForUser.username}
                                            </p>
                                            <p>
                                                <strong>Password:</strong> ********
                                            </p>
                                        </>
                                    )}
                                    <button
                                        onClick={handleDisconnect}
                                        className={`${linkStyle} underline`}>
                                        <span className="text-sm">
                                            Disconnect provider
                                        </span>
                                    </button>
                                </div>
                            ) : (
                                <PrimaryButton onClick={handleConnectSmtp}>
                                    Connect email provider
                                </PrimaryButton>
                            )}
                        </div>
                    )}
                </QueryRenderer>
                <Panel
                    isOpen={showPanel}
                    headerText="Connect email provider"
                    type={PanelType.large}
                    onDismiss={handleDismiss}>
                    {showPanel ? (
                        <CreateSmtpIntegration
                            onCancel={handleDismiss}
                            onSaved={handleSaved}
                        />
                    ) : null}
                </Panel>
            </ContentFull>
        </div>
    );
};

interface CreateMailboxIntegrationProps {}

const CreateMailboxIntegration: React.FC<CreateMailboxIntegrationProps> = props => {
    const productName = useProductName();

    const mailboxSyncEnabled = useFeature("mailboxSync");

    const deleteMailbox = useDeleteUserMailboxMutation();

    const getMailboxes = useGetUserMailboxesQuery({}, { enabled: mailboxSyncEnabled });

    const mailboxes = useMemo(() => {
        if (getMailboxes.data) {
            return getMailboxes.data.getUserMailboxes;
        }
        return [];
    }, [getMailboxes.data]);

    const handleDisconnect = React.useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();

            const confirmed = await fancyConfirm(
                "Disconnect provider?",
                "Are you sure you want to disconnect the mailbox integration? Emails will no longer be automatically added to your clients, communities, and referral sources.",
                "Yes, disconnect",
                "Cancel",
            );

            if (!confirmed) {
                return;
            }

            await deleteMailbox.mutateAsync({ id: mailboxes[0].id });
            getMailboxes.refetch();
        },
        [mailboxes, deleteMailbox, getMailboxes],
    );

    const handleRetry = React.useCallback(
        async (mailboxId: UserMailboxId) => {
            await deleteMailbox.mutateAsync({ id: mailboxId });
            window.location.href = "/api/auth/mailsync";
        },
        [deleteMailbox],
    );

    if (!mailboxSyncEnabled) {
        return null;
    }

    const activeMailbox = mailboxes.find(m => m.status === UserMailboxStatus.Active);
    const pendingMailbox = mailboxes.find(
        m =>
            m.status === UserMailboxStatus.Authenticating ||
            m.status === UserMailboxStatus.Authenticated,
    );
    const disconnectedMailbox = mailboxes.find(
        m => m.status === UserMailboxStatus.Disconnected,
    );

    return (
        <ContentFull>
            <ContentHeader>Mailbox Sync</ContentHeader>
            <ContentSubheader>
                Sync your email inbox to {productName} to have emails automatically added
                to your clients, communities, and referral sources.
            </ContentSubheader>
            {activeMailbox ? (
                <>
                    <InlineBanner type="success">
                        Your mailbox is connected. Emails will be automatically added to
                        your clients, communities, and referral sources.
                    </InlineBanner>
                    <button onClick={handleDisconnect} className={linkStyle}>
                        <span className="text-sm">Disconnect provider</span>
                    </button>
                </>
            ) : pendingMailbox ? (
                <>
                    <InlineBanner type="warning">
                        Mailbox setup is in progress. If this persists, try reconnecting.
                    </InlineBanner>
                    <button
                        onClick={() => handleRetry(pendingMailbox.id)}
                        className={linkStyle}>
                        <span className="text-sm">Retry connection</span>
                    </button>
                </>
            ) : disconnectedMailbox ? (
                <>
                    <InlineBanner type="error">
                        Connection to your mailbox failed. Please try reconnecting.
                    </InlineBanner>
                    <button
                        onClick={() => handleRetry(disconnectedMailbox.id)}
                        className={linkStyle}>
                        <span className="text-sm">Reconnect</span>
                    </button>
                </>
            ) : (
                <a href="/api/auth/mailsync" className={primaryClasses}>
                    Connect Mailbox
                </a>
            )}
        </ContentFull>
    );
};

interface CreateSmtpIntegrationProps {
    onSaved: () => void;
    onCancel: () => void;
}

interface EmailProvider {
    key: string;
    label: string;
    host: string;
    port: string;
    type: "managed" | "manual" | "api";
    appPasswordLink?: string;
    authUrl?: string;
}

const providers: EmailProvider[] = [
    {
        key: "gmail",
        label: "Google Workspace/Gmail",
        host: "smtp.gmail.com",
        port: "587",
        type: "managed",
        appPasswordLink: "https://myaccount.google.com/apppasswords",
    },
    {
        key: "outlook",
        label: "Outlook.com",
        host: "",
        port: "",
        type: "api",
        authUrl: "/api/auth/outlook",
    },
    { key: "other", label: "Other Provider", type: "manual", host: "", port: "" },
];

const CreateSmtpIntegration: React.FC<CreateSmtpIntegrationProps> = props => {
    const { onCancel, onSaved } = props;

    const [selectedProvider, setSelectedProvider] = React.useState<EmailProvider>(
        providers[0],
    );
    const [smtpServer, setSmtpServer] = React.useState<string>(selectedProvider.host);
    const [smtpPort, setSmtpPort] = React.useState<string>(selectedProvider.port);
    const [username, setUsername] = React.useState<string>("");
    const [password, setPassword] = React.useState<string>("");
    const [error, setError] = React.useState<string | null>(null);

    const createSmtpCredential = useCreateSmtpCredentialMutation();

    const handleSave = React.useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            const port = parseInt(smtpPort, 10);
            if (isNaN(port) || port < 1 || port > 65535) {
                setError("Invalid port number");
                return;
            }
            setError(null);
            const result = await createSmtpCredential.mutateAsync({
                params: {
                    host: smtpServer,
                    port,
                    username,
                    password,
                },
            });
            if (result.createSmtpCredential.success) {
                onSaved();
            } else {
                setError(result.createSmtpCredential.message);
            }
        },
        [smtpServer, smtpPort, username, password, onSaved, createSmtpCredential],
    );

    const handleCancel = React.useCallback(() => {
        onCancel();
    }, [onCancel]);

    const handleProviderChange = React.useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const provider = providers.find(p => p.key === e.target.value);
            if (provider) {
                setSelectedProvider(provider);
                if (provider.type === "managed") {
                    setSmtpServer(provider.host);
                    setSmtpPort(provider.port);
                } else {
                    setSmtpServer("");
                    setSmtpPort("");
                }
            }
        },
        [setSelectedProvider, setSmtpPort, setSmtpServer],
    );

    const productName = useProductName();
    const autoComplete = useAutoComplete();
    const whiteLabelContentMode = useWhiteLabelContentMode();

    return (
        <div className="space-y-4">
            <p>{`Provide your email provider's SMTP credentials to have ${productName} send emails directly using your email provider.`}</p>
            <Select
                label="Email Provider"
                value={selectedProvider.key}
                onChange={handleProviderChange}>
                {providers.map(p => (
                    <option key={p.key} value={p.key}>
                        {p.label}
                    </option>
                ))}
            </Select>
            {selectedProvider.type === "api" ? null : (
                <>
                    <Input
                        disabled={selectedProvider.type === "managed"}
                        label="SMTP Server"
                        value={smtpServer}
                        onChange={e => setSmtpServer(e.target.value)}
                    />
                    <Input
                        disabled={selectedProvider.type === "managed"}
                        label="SMTP Port"
                        value={smtpPort}
                        onChange={e => setSmtpPort(e.target.value)}
                    />
                </>
            )}
            {selectedProvider.appPasswordLink ? (
                <InlineBanner type="info">
                    For security purposes Google requires users to complete multi-factor
                    authentication, and generate an app password to connect via SMTP.
                    Please use the documentation below to set up your app password. Once
                    that is set up, use your email address as your username and the app
                    password as your password. <br />
                    <br />
                    {whiteLabelContentMode !== WhiteLabelContentMode.Restricted ? (
                        <li>
                            <DocLink
                                path="/article/130-how-to-set-up-direct-email-smtp"
                                className="underline">
                                View our SMTP guide
                            </DocLink>
                        </li>
                    ) : null}
                    <li>
                        <a
                            href={selectedProvider.appPasswordLink}
                            target="_blank"
                            rel="noreferrer"
                            className="underline">
                            {"Set up your Google app password"}
                        </a>
                    </li>
                </InlineBanner>
            ) : null}
            {selectedProvider.authUrl ? (
                <InlineBanner type="info">
                    In order to set up the {selectedProvider.label} SMTP integration,
                    please click <strong>Connect to {selectedProvider.label}</strong>{" "}
                    below. You will be taken to your {selectedProvider.label} account and
                    will be asked to give permission to {productName} in order to access
                    your data. Once that is complete, you will be returned to{" "}
                    {productName}. <br />
                    <br />
                    <a href={selectedProvider.authUrl} className={primaryClasses}>
                        Connect to {selectedProvider.label}
                    </a>
                </InlineBanner>
            ) : (
                <>
                    <Input
                        autoComplete={autoComplete}
                        label="Username"
                        value={username}
                        onChange={e => setUsername(e.target.value)}
                    />
                    <Input
                        autoComplete={autoComplete}
                        label="Password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        type="password"
                    />
                    {error ? <InlineBanner type="error">{error}</InlineBanner> : null}
                    <div className="flex justify-end items-center space-x-4">
                        {createSmtpCredential.isLoading ? <Spinner /> : null}
                        <PrimaryButton
                            disabled={createSmtpCredential.isLoading}
                            onClick={handleSave}>
                            Test connection &amp; save
                        </PrimaryButton>
                        <SecondaryButton
                            disabled={createSmtpCredential.isLoading}
                            onClick={handleCancel}>
                            Cancel
                        </SecondaryButton>
                    </div>
                </>
            )}
        </div>
    );
};
